/** Canonical Help Center defaults — UI fallbacks when /api/v1/help is unavailable.
 *  Keep in sync with backend/src/content/helpCenterDefaults.ts (helpCenterDefaultsSync.test.ts). */

export interface HelpArticleDefault {
  slug: string
  title: string
  section: string
  body: string
  tags: string[]
  isVideo?: boolean
  videoDuration?: string
  tipType?: 'info' | 'warning' | 'success'
}

export interface HelpFaqDefault {
  slug: string
  question: string
  answer: string
  section: string
}

export const IMPORT_COLLECTION_ANSWER =
  'Yes! Open the Import Collection page from the sidebar, or go to Settings > Data Import & Export and click "Import CSV". Our support team can help with large imports or custom formats.'

export const MOBILE_APP_ANSWER =
  "CigarTrack is fully optimized for mobile browsers on any phone or tablet. Add it to your home screen via your browser's Share menu for quick access. Installable app (PWA) support is planned for a future release."

export const OFFLINE_ANSWER =
  'CigarTrack stores your collection locally so you can browse it without an internet connection. Changes made offline sync automatically when your connection returns. Full offline-first app install (PWA) support is planned for a future release.'

export const DATA_BACKUP_ANSWER =
  'You can download a full JSON backup of your collection anytime from Settings > Data Import & Export > Download Backup. We recommend doing this regularly for your own records.'

export const DATA_EXPORT_ANSWER =
  'Export your collection as a CSV or download a full JSON backup from Settings > Data Import & Export. Use "Export CSV" for spreadsheet-compatible data and "Download Backup" for a complete JSON snapshot you can restore later.'

export const CIGAR_AGING_ANSWER =
  'CigarTrack tracks how long each cigar has been in your collection. Aging goal notifications are planned for a future release.'

export const SHARE_COLLECTION_ANSWER =
  'Your collection is private to your account today. Sharing individual cigars, humidors, or your full collection is planned for a future release.'

export const CANCEL_SUBSCRIPTION_ANSWER =
  'Open Settings and choose Manage Subscription (or go to /app/settings/subscription while signed in). Use Downgrade to Free to return to the free tier. Paid billing is not integrated yet — joining a paid waitlist does not charge you. Contact support via Report Issue (/app/feedback) if you need help.'

export const PRICING_PLANS_ANSWER =
  'The Free plan is available today (1 humidor, up to 100 cigars). Premium ($9.99/mo) and Pro ($19.99/mo) are planned — join the waitlist from the Pricing page. Stripe checkout and paid trials are not live yet.'

export const COLLECTION_VALUE_ANSWER =
  'CigarTrack shows your current collection value and average price per cigar from purchase prices on the Collection and Analytics pages. Value-over-time charts and premium spending breakdowns are planned for a future release.'

export const REFUNDS_ANSWER =
  'Paid billing is not integrated yet, so refunds do not apply today. When Premium and Pro launch, refund terms will be published before checkout goes live. Contact support via the Contact page or Report Issue (/app/feedback) with questions.'

export const PAYMENT_METHODS_ANSWER =
  'We plan to accept major credit cards via Stripe when paid plans launch. Checkout is not available today — the Free plan requires no payment.'

export const PASSWORD_SECURITY_ANSWER =
  'Change your password from Settings while signed in, or use Forgot Password on the login page. Two-factor authentication is planned for a future release.'

export const PHOTO_UPLOAD_ANSWER =
  'Smoking sessions support image URLs in the session form (paste a link to a hosted photo). File uploads for cigars and sessions are planned for a future release.'

export const ADD_CIGAR_ANSWER =
  'Navigate to Collection, select a humidor, and click Add Cigar. Search the community database for brand and line, then enter vitola, purchase price, and notes. Cigar photo file upload is planned for a future release.'

export const DATA_SECURITY_ANSWER =
  'Data is transmitted over HTTPS and stored in secured cloud infrastructure. We do not sell your personal data. Formal compliance certifications (e.g. SOC 2) are on the roadmap as we scale.'

export const GDPR_PRIVACY_ANSWER =
  'Export your data from Settings > Data Import & Export and delete your account from Settings. Contact support for additional privacy requests; expanded GDPR documentation is planned as we scale.'

export const CONTEXTUAL_EXPORT_TIP =
  'Tip: Export your data regularly from Settings > Data Import & Export. Use "Download Backup" for a full JSON snapshot you can restore later.'

function sectionTag(section: string): string {
  return `section:${section}`
}

export const HELP_CONTEXTUAL_TIPS = [
  'Tip: Use the Cigar Database to quickly find and add cigars to your collection without entering all details manually.',
  'Tip: Set humidity and temperature alerts in humidor settings to protect your investment.',
  'Tip: Add tasting notes immediately after smoking for the most accurate flavor memories.',
  'Tip: Use the Analytics page to discover your favorite brands and spending patterns.',
  CONTEXTUAL_EXPORT_TIP,
]

export const HELP_ARTICLE_DEFAULTS: HelpArticleDefault[] = [
  {
    slug: 'getting-started-1',
    title: 'Getting Started with CigarTrack',
    section: 'Getting Started',
    body: 'Learn how to set up your account and create your first humidor. This guide walks you through the initial setup, adding your first cigars, and organizing your collection.',
    tags: [sectionTag('Getting Started'), 'setup', 'beginner', 'humidor', 'collection'],
  },
  {
    slug: 'getting-started-2',
    title: 'Quick Tour: Navigating the Dashboard',
    section: 'Getting Started',
    body: 'A quick overview of the CigarTrack dashboard and all its features. Understand how to access your collection, journal, and analytics from one central location.',
    tags: [sectionTag('Getting Started'), 'dashboard', 'navigation', 'overview', 'video', 'duration:3:45'],
    isVideo: true,
    videoDuration: '3:45',
  },
  {
    slug: 'collection-1',
    title: 'Managing Your Cigar Collection',
    section: 'Feature Guides',
    body: 'Learn to add, edit, and organize cigars in your collection. Discover how to use humidors for organization and track inventory levels.',
    tags: [sectionTag('Feature Guides'), 'collection', 'humidor', 'organize', 'inventory'],
  },
  {
    slug: 'collection-2',
    title: 'Importing Cigars via CSV',
    section: 'Feature Guides',
    body: 'Step-by-step guide to importing your existing cigar collection using a CSV file. Open the Import Collection page from the sidebar, or go to Settings > Data Import & Export > Import CSV. Includes format requirements and tips for a smooth import.',
    tags: [sectionTag('Feature Guides'), 'import', 'csv', 'bulk', 'collection'],
  },
  {
    slug: 'journal-1',
    title: 'Recording Smoking Sessions',
    section: 'Feature Guides',
    body: 'Detailed walkthrough of the Smoking Journal. Learn to record sessions, add tasting notes, rate cigars, attach photos, and build your personal smoking history.',
    tags: [sectionTag('Feature Guides'), 'journal', 'tasting notes', 'rating', 'sessions'],
  },
  {
    slug: 'journal-2',
    title: 'Smoking Journal Tutorial',
    section: 'Feature Guides',
    body: 'Video walkthrough showing how to log your first smoking session, rate cigars, and add detailed tasting notes for future reference.',
    tags: [sectionTag('Feature Guides'), 'journal', 'tutorial', 'tasting notes', 'video', 'duration:5:12'],
    isVideo: true,
    videoDuration: '5:12',
  },
  {
    slug: 'analytics-1',
    title: 'Understanding Your Analytics',
    section: 'Feature Guides',
    body: 'Explore your collection analytics, including spending trends, most smoked brands, aging statistics, and collection value tracking.',
    tags: [sectionTag('Feature Guides'), 'analytics', 'statistics', 'value', 'trends'],
  },
  {
    slug: 'analytics-2',
    title: 'Analytics Deep Dive',
    section: 'Feature Guides',
    body: 'Video guide to all analytics features including custom date ranges, export options, and how to interpret collection metrics.',
    tags: [sectionTag('Feature Guides'), 'analytics', 'charts', 'reports', 'video', 'duration:7:30'],
    isVideo: true,
    videoDuration: '7:30',
  },
  {
    slug: 'database-1',
    title: 'Searching the Cigar Database',
    section: 'Feature Guides',
    body: 'Learn to search the comprehensive cigar database to find brands, lines, and vitolas. Discover how to add database entries to your personal collection.',
    tags: [sectionTag('Feature Guides'), 'database', 'search', 'brands', 'vitola'],
  },
  {
    slug: 'troubleshoot-1',
    title: 'Fixing Slow Performance',
    section: 'Troubleshooting',
    body: 'If CigarTrack is running slowly, try these steps: clear your browser cache, check your internet connection, disable browser extensions, or try a different browser.',
    tags: [sectionTag('Troubleshooting'), 'performance', 'slow', 'cache', 'browser', 'tip:warning'],
    tipType: 'warning',
  },
  {
    slug: 'troubleshoot-2',
    title: 'Photo Upload Issues',
    section: 'Troubleshooting',
    body: 'Unable to upload photos? Ensure images are under 10MB and in JPG, PNG, or WebP format. Check your internet connection and try again with a smaller image.',
    tags: [sectionTag('Troubleshooting'), 'photos', 'upload', 'images', 'error', 'tip:warning'],
    tipType: 'warning',
  },
  {
    slug: 'troubleshoot-3',
    title: 'Data Import, Export & Backup',
    section: 'Troubleshooting',
    body: DATA_EXPORT_ANSWER,
    tags: [sectionTag('Troubleshooting'), 'export', 'backup', 'data', 'csv', 'json'],
  },
  {
    slug: 'mobile-1',
    title: 'Using CigarTrack on Mobile',
    section: 'Feature Guides',
    body: MOBILE_APP_ANSWER,
    tags: [sectionTag('Feature Guides'), 'mobile', 'phone', 'tablet', 'home screen', 'tip:info'],
    tipType: 'info',
  },
  {
    slug: 'mobile-2',
    title: 'Mobile Navigation Tutorial',
    section: 'Feature Guides',
    body: 'Video walkthrough of the CigarTrack mobile experience — navigating your collection, logging a smoking session, and checking analytics from a phone or tablet.',
    tags: [sectionTag('Feature Guides'), 'mobile', 'navigation', 'tutorial', 'ios', 'android', 'video', 'duration:4:15'],
    isVideo: true,
    videoDuration: '4:15',
  },
]

export const HELP_FAQ_DEFAULTS: HelpFaqDefault[] = [
  {
    slug: 'faq-1',
    question: 'How do I create my first humidor?',
    answer:
      'Go to the Humidors page and click "Add Humidor". Enter the name, capacity, and location. You can add multiple humidors to organize by location or type.',
    section: 'Getting Started',
  },
  {
    slug: 'faq-2',
    question: 'Can I import my existing collection?',
    answer: IMPORT_COLLECTION_ANSWER,
    section: 'Getting Started',
  },
  {
    slug: 'faq-3',
    question: 'How does cigar aging tracking work?',
    answer: CIGAR_AGING_ANSWER,
    section: 'Features',
  },
  {
    slug: 'faq-4',
    question: 'Can I share my collection with friends?',
    answer: SHARE_COLLECTION_ANSWER,
    section: 'Features',
  },
  {
    slug: 'faq-5',
    question: 'How do I track my collection value?',
    answer:
      'CigarTrack calculates collection value from purchase prices. Premium users get advanced analytics including value trends and spending analysis.',
    section: 'Features',
  },
  {
    slug: 'faq-6',
    question: 'Is my data backed up?',
    answer: DATA_BACKUP_ANSWER,
    section: 'Technical',
  },
  {
    slug: 'faq-7',
    question: 'Can I use CigarTrack offline?',
    answer: OFFLINE_ANSWER,
    section: 'Technical',
  },
  {
    slug: 'faq-8',
    question: 'How do I cancel my subscription?',
    answer: CANCEL_SUBSCRIPTION_ANSWER,
    section: 'Account',
  },
]

export const HELP_BASELINE_ITEM_COUNT = HELP_ARTICLE_DEFAULTS.length + HELP_FAQ_DEFAULTS.length

export interface HelpCenterArticle {
  id: string
  slug: string
  title: string
  category: string
  content: string
  tags: string[]
  isVideo?: boolean
  videoDuration?: string
  tipType?: 'info' | 'warning' | 'success'
}

export interface HelpCenterFaq {
  id: string
  slug: string
  question: string
  answer: string
  category: string
}

export interface HelpCenterPayload {
  articles: HelpCenterArticle[]
  faqs: HelpCenterFaq[]
  contextualTips: string[]
  source: 'database' | 'defaults' | 'merged'
}

export function buildDefaultHelpCenterPayload(): HelpCenterPayload {
  return {
    articles: HELP_ARTICLE_DEFAULTS.map((a) => ({
      id: a.slug,
      slug: a.slug,
      title: a.title,
      category: a.section,
      content: a.body,
      tags: a.tags.filter(
        (t) => !t.startsWith('section:') && t !== 'video' && !t.startsWith('duration:') && !t.startsWith('tip:')
      ),
      ...(a.isVideo ? { isVideo: true, videoDuration: a.videoDuration } : {}),
      ...(a.tipType ? { tipType: a.tipType } : {}),
    })),
    faqs: HELP_FAQ_DEFAULTS.map((f) => ({
      id: f.slug,
      slug: f.slug,
      question: f.question,
      answer: f.answer,
      category: f.section,
    })),
    contextualTips: HELP_CONTEXTUAL_TIPS,
    source: 'defaults',
  }
}

/** Map FAQ/CMS section labels to Help Center UI category chips */
export function faqMatchesUiCategory(faqSection: string, uiCategory: string | null): boolean {
  if (!uiCategory || uiCategory === 'FAQ') return true
  const groups: Record<string, string[]> = {
    'Getting Started': ['Getting Started'],
    'Feature Guides': ['Features', 'Feature Guides'],
    Troubleshooting: ['Troubleshooting', 'Technical', 'Account'],
  }
  const allowed = groups[uiCategory]
  return allowed ? allowed.includes(faqSection) : faqSection === uiCategory
}

export function articleMatchesUiCategory(articleCategory: string, uiCategory: string | null): boolean {
  if (!uiCategory || uiCategory === 'FAQ') return uiCategory !== 'FAQ'
  return articleCategory === uiCategory
}
